import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import api from '@/api/client'
import { ArrowLeft, ArrowRight, Database, Link2, Clock, Globe, FileText, Shield, Landmark, CreditCard, Languages, Hash } from 'lucide-react'

interface RelationData {
  display_name: string
  type: 'belongs_to' | 'has_many'
  ref_table?: string
  table?: string
  fk_column: string
  data: Record<string, unknown> | Record<string, unknown>[] | null
  count?: number
}

interface DetailResponse {
  data: Record<string, unknown>
  relations: RelationData[] | null
}

const TABLE_LABELS: Record<string, string> = {
  countries: 'Countries',
  currencies: 'Currencies',
  languages: 'Languages',
  document_types: 'Document Types',
  country_document_types: 'Country Document Types',
  gaming_jurisdictions: 'Gaming Jurisdictions',
  jurisdiction_countries: 'Jurisdiction Countries',
  timezones: 'Timezones',
  banks: 'Banks',
  payment_methods: 'Payment Methods',
  bank_payment_methods: 'Bank Payment Methods',
  country_payment_methods: 'Country Payment Methods',
  country_languages: 'Country Languages',
  bank_account_formats: 'Bank Account Formats',
  country_bank_account_formats: 'Country Account Formats',
  products: 'Products',
  permissions: 'Permissions',
  roles: 'Roles',
  role_permissions: 'Role Permissions',
}

const TABLE_PK: Record<string, string> = {
  countries: 'code',
  currencies: 'code',
  languages: 'code',
  document_types: 'id',
  gaming_jurisdictions: 'id',
  country_document_types: 'country_code',
  jurisdiction_countries: 'jurisdiction_id',
  timezones: 'id',
  banks: 'id',
  payment_methods: 'id',
  bank_payment_methods: 'bank_id',
  country_payment_methods: 'country_code',
  bank_account_formats: 'id',
  country_bank_account_formats: 'country_code',
  products: 'id',
  permissions: 'id',
  roles: 'id',
  role_permissions: 'role_id',
}

// Icons for relation display names
const RELATION_ICONS: Record<string, typeof Globe> = {
  'Default Currency': Globe,
  'Required Documents': FileText,
  'Gaming Jurisdictions': Shield,
  'Timezones': Clock,
  'Banks': Landmark,
  'Payment Methods': CreditCard,
  'Languages': Languages,
  'Country': Globe,
  'Jurisdiction': Shield,
  'Document Type': FileText,
  'Payment Method': CreditCard,
  'Bank': Landmark,
  'Banks Supporting This Method': Landmark,
  'Countries With This Method': Globe,
  'Countries Requiring This Document': Globe,
  'Countries in Jurisdiction': Globe,
  'Countries Using This Currency': Globe,
  'Bank Account Formats': Hash,
  'Countries Using This Format': Globe,
  'Format': Hash,
}

// Junction tables that need resolution
const JUNCTION_TABLES = new Set([
  'country_document_types',
  'jurisdiction_countries',
  'bank_payment_methods',
  'country_payment_methods',
  'country_languages',
  'country_bank_account_formats',
])

function countryFlag(code: unknown): string {
  if (!code || typeof code !== 'string' || code.length !== 2) return ''
  const upper = code.toUpperCase()
  const codePoints = [...upper].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  return String.fromCodePoint(...codePoints)
}

function getDisplayValue(row: Record<string, unknown> | null | undefined): string {
  if (!row) return '—'
  return String(row['name'] || row['code'] || row['id'] || Object.values(row)[0] || '—')
}

function getDisplayWithFlag(row: Record<string, unknown> | null | undefined, tableName?: string): string {
  if (!row) return '—'
  const name = getDisplayValue(row)
  if (tableName === 'countries' || row['code_3'] !== undefined) {
    const flag = countryFlag(row['code'])
    return flag ? `${flag} ${name}` : name
  }
  return name
}

function getPK(tableName: string, row: Record<string, unknown> | null | undefined): string {
  if (!row) return ''
  const pk = TABLE_PK[tableName] || 'id'
  return String(row[pk] ?? '')
}

const FIELD_LABELS: Record<string, string> = {
  code: 'Code', code_3: 'ISO Alpha-3', name: 'Name', native_name: 'Native Name',
  numeric_code: 'Numeric Code', phone_prefix: 'Phone Prefix', is_active: 'Active',
  default_currency_code: 'Default Currency', symbol: 'Symbol', decimal_places: 'Decimal Places',
  id: 'ID', description: 'Description', is_kyc_required: 'KYC Required', is_mandatory: 'Mandatory',
  country_code: 'Country', document_type_id: 'Document Type', jurisdiction_id: 'Jurisdiction',
  license_name: 'License Name', regulatory_body: 'Regulatory Body', website: 'Website',
  utc_offset: 'UTC Offset', utc_offset_dst: 'UTC Offset (DST)',
  abbreviation: 'Abbreviation', abbreviation_dst: 'Abbreviation (DST)',
  swift_code: 'SWIFT Code', bank_code: 'Bank Code', type: 'Type',
  is_deposit: 'Deposit', is_withdrawal: 'Withdrawal', min_amount: 'Min Amount', max_amount: 'Max Amount',
  processing_time: 'Processing Time', fee_percentage: 'Fee %', fee_fixed: 'Fixed Fee',
  is_default: 'Default', is_primary: 'Primary', is_official: 'Official',
  validation_regex: 'Validation Regex', length: 'Length', example: 'Example',
  bank_account_format_id: 'Account Format',
}

// Fields to hide from the detail view
const HIDDEN_FIELDS = new Set(['preview_url'])

export function CatalogDetailPage() {
  const { table, pk } = useParams<{ table: string; pk: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<DetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!table || !pk) return
    setLoading(true)
    setError(null)
    api.get(`/catalog/${table}/${pk}`)
      .then(({ data }) => {
        if (data && data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
          setDetail({ data: data.data, relations: data.relations || null })
        } else {
          setDetail({ data: data, relations: null })
        }
      })
      .catch(() => setError('Record not found'))
      .finally(() => setLoading(false))
  }, [table, pk])

  if (!table || !pk) return null

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading...</div>
  }

  if (error || !detail) {
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-4">{error || 'Not found'}</p>
        <button onClick={() => navigate(-1)} className="text-accent hover:underline">Go back</button>
      </div>
    )
  }

  const { data: row, relations } = detail
  const isCountryTable = table === 'countries'

  // Split relations into belongs_to and has_many
  const belongsTo = relations?.filter(r => r.type === 'belongs_to') || []
  const hasMany = relations?.filter(r => r.type === 'has_many') || []
  const withData = hasMany.filter(r => (r.count ?? 0) > 0)
  const withoutData = hasMany.filter(r => (r.count ?? 0) === 0)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate(`/catalog/${table}`)} className="p-2 hover:bg-card rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <div className="flex items-center gap-3">
          {isCountryTable && <span className="text-5xl leading-none">{countryFlag(row['code'])}</span>}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{TABLE_LABELS[table] || table}</p>
            <h1 className="text-2xl font-bold text-foreground">{getDisplayValue(row)}</h1>
            {isCountryTable && row['native_name'] && row['native_name'] !== row['name'] && (
              <p className="text-sm text-muted-foreground">{String(row['native_name'])}</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick stats for countries */}
      {isCountryTable && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {hasMany.map((rel) => {
            const Icon = RELATION_ICONS[rel.display_name] || Link2
            return (
              <div key={rel.display_name} className="bg-card border border-border rounded-xl p-3 text-center">
                <Icon className="h-4 w-4 text-accent mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{rel.count ?? 0}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight">{rel.display_name}</p>
              </div>
            )
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Fields + belongs_to */}
        <div className="lg:col-span-4 space-y-4">
          {/* Fields card */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-muted/20">
              <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Database className="h-3.5 w-3.5 text-accent" />
                Properties
              </h2>
            </div>
            <div className="divide-y divide-border/30">
              {Object.entries(row)
                .filter(([key]) => !HIDDEN_FIELDS.has(key))
                .map(([key, value]) => (
                <div key={key} className="flex items-start px-4 py-2.5 gap-3">
                  <span className="text-[11px] text-muted-foreground w-32 shrink-0 font-medium uppercase tracking-wider pt-0.5">
                    {FIELD_LABELS[key] || key.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm text-foreground break-all flex-1">
                    {renderFieldValue(key, value, table)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Belongs-to cards */}
          {belongsTo.map((rel, i) => (
            <BelongsToCard key={i} relation={rel} />
          ))}

          {/* Empty relations summary */}
          {withoutData.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">No data</p>
              <div className="flex flex-wrap gap-2">
                {withoutData.map((rel) => (
                  <span key={rel.display_name} className="text-xs text-muted-foreground/60 bg-muted/30 px-2 py-1 rounded">
                    {rel.display_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Has-many relations */}
        <div className="lg:col-span-8 space-y-4">
          {withData.length > 0 ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {withData.map((rel, i) => (
                <HasManyCard key={i} relation={rel} currentTable={table} />
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <p className="text-sm text-muted-foreground">No related records</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function renderFieldValue(key: string, value: unknown, table: string) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/50 italic text-xs">null</span>
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${value ? 'bg-green-500/10 text-green-400' : 'bg-muted text-muted-foreground'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    )
  }
  if (key === 'country_code' || (key === 'code' && table === 'countries')) {
    const flag = countryFlag(value)
    return flag ? <>{flag} {String(value)}</> : String(value)
  }
  if (key === 'default_currency_code' && value) {
    return (
      <Link to={`/catalog/currencies/${value}`} className="text-accent hover:underline text-xs font-medium">
        {String(value)}
      </Link>
    )
  }
  if (key === 'website' && typeof value === 'string' && value.startsWith('http')) {
    return (
      <a href={String(value)} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-xs break-all">
        {String(value)}
      </a>
    )
  }
  return String(value)
}

function BelongsToCard({ relation }: { relation: RelationData }) {
  const refTable = relation.ref_table || ''
  const row = relation.data as Record<string, unknown> | null
  const Icon = RELATION_ICONS[relation.display_name] || Link2

  if (!row) {
    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-accent" />
          {relation.display_name}
        </h3>
        <p className="text-sm text-muted-foreground/50 italic">Not set</p>
      </div>
    )
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-accent" />
        {relation.display_name}
      </h3>
      <Link
        to={`/catalog/${refTable}/${getPK(refTable, row)}`}
        className="flex items-center justify-between p-2.5 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group"
      >
        <div>
          <p className="text-sm font-medium text-foreground">{getDisplayWithFlag(row, refTable)}</p>
          <p className="text-[10px] text-muted-foreground">{TABLE_LABELS[refTable] || refTable}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
      </Link>
    </div>
  )
}

function HasManyCard({ relation, currentTable }: { relation: RelationData; currentTable: string }) {
  const relTable = relation.table || ''
  const rows = (relation.data as Record<string, unknown>[] | null) || []
  const Icon = RELATION_ICONS[relation.display_name] || Link2

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-muted/20 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-accent" />
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex-1">
          {relation.display_name}
        </h3>
        {relation.count !== undefined && (
          <span className="text-[10px] bg-accent/20 text-accent px-2 py-0.5 rounded-full font-bold">
            {relation.count}
          </span>
        )}
      </div>
      <div className="divide-y divide-border/30 max-h-72 overflow-y-auto">
        {rows.map((row, i) => {
          const { linkTo, label } = resolveLink(row, relTable, currentTable)
          return (
            <Link
              key={i}
              to={linkTo}
              className="flex items-center justify-between px-4 py-2 hover:bg-muted/20 transition-colors group"
            >
              <span className="text-sm text-foreground truncate">{label}</span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-accent transition-colors shrink-0 ml-2" />
            </Link>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Resolves the link and label for a related row.
 * For junction tables, shows the resolved name from the "other" side.
 * For direct has_many, shows the row's own display value.
 */
function resolveLink(
  row: Record<string, unknown>,
  relTable: string,
  currentTable: string,
): { linkTo: string; label: string } {
  // Direct has_many (not a junction table)
  if (!JUNCTION_TABLES.has(relTable)) {
    return {
      linkTo: `/catalog/${relTable}/${getPK(relTable, row)}`,
      label: getDisplayWithFlag(row, relTable),
    }
  }

  // Generic junction resolution: find _resolved_* keys and use the "other" side
  const resolvedKeys = Object.keys(row).filter(k => k.startsWith('_resolved_'))

  for (const rk of resolvedKeys) {
    const fkCol = rk.replace('_resolved_', '')
    const resolved = row[rk] as Record<string, unknown> | undefined
    if (!resolved) continue

    // Determine which table this FK points to
    const targetTable = guessTableForFK(fkCol)
    if (!targetTable) continue

    // Skip if the resolved row points back to the current table
    if (targetTable === currentTable) continue

    const name = getDisplayWithFlag(resolved, targetTable)
    const pk = getPK(targetTable, resolved)
    return { linkTo: `/catalog/${targetTable}/${pk}`, label: name }
  }

  // Fallback: use raw FK values
  if (relTable === 'country_document_types') {
    if (currentTable === 'countries') {
      return { linkTo: `/catalog/document_types/${row['document_type_id']}`, label: `Document Type #${row['document_type_id']}` }
    }
    return { linkTo: `/catalog/countries/${row['country_code']}`, label: `${countryFlag(row['country_code'])} ${row['country_code']}` }
  }
  if (relTable === 'jurisdiction_countries') {
    if (currentTable === 'countries') {
      return { linkTo: `/catalog/gaming_jurisdictions/${row['jurisdiction_id']}`, label: `Jurisdiction #${row['jurisdiction_id']}` }
    }
    return { linkTo: `/catalog/countries/${row['country_code']}`, label: `${countryFlag(row['country_code'])} ${row['country_code']}` }
  }
  if (relTable === 'country_payment_methods') {
    if (currentTable === 'countries') {
      return { linkTo: `/catalog/payment_methods/${row['payment_method_id']}`, label: `Payment Method #${row['payment_method_id']}` }
    }
    return { linkTo: `/catalog/countries/${row['country_code']}`, label: `${countryFlag(row['country_code'])} ${row['country_code']}` }
  }
  if (relTable === 'bank_payment_methods') {
    if (currentTable === 'banks') {
      return { linkTo: `/catalog/payment_methods/${row['payment_method_id']}`, label: `Payment Method #${row['payment_method_id']}` }
    }
    return { linkTo: `/catalog/banks/${row['bank_id']}`, label: `Bank #${row['bank_id']}` }
  }
  if (relTable === 'country_languages') {
    if (currentTable === 'countries') {
      return { linkTo: `/catalog/languages/${row['language_code']}`, label: `${row['language_code']}` }
    }
    return { linkTo: `/catalog/countries/${row['country_code']}`, label: `${countryFlag(row['country_code'])} ${row['country_code']}` }
  }
  if (relTable === 'country_bank_account_formats') {
    if (currentTable === 'countries') {
      return { linkTo: `/catalog/bank_account_formats/${row['bank_account_format_id']}`, label: `Format #${row['bank_account_format_id']}` }
    }
    return { linkTo: `/catalog/countries/${row['country_code']}`, label: `${countryFlag(row['country_code'])} ${row['country_code']}` }
  }

  return {
    linkTo: `/catalog/${relTable}/${getPK(relTable, row)}`,
    label: getDisplayValue(row),
  }
}

function guessTableForFK(fkCol: string): string | null {
  const map: Record<string, string> = {
    'country_code': 'countries',
    'document_type_id': 'document_types',
    'jurisdiction_id': 'gaming_jurisdictions',
    'payment_method_id': 'payment_methods',
    'bank_id': 'banks',
    'language_code': 'languages',
    'bank_account_format_id': 'bank_account_formats',
  }
  return map[fkCol] || null
}
